// Mirrors the Datadog Browser SDK session store (browser-core domain/session) so the sampling decision can be
// made before the SDK is downloaded, and stays the one the SDK itself would read and keep.
const SESSION_COOKIE_NAME = '_dd_s';
const SESSION_ENTRY_SEPARATOR = '&';
const SESSION_ENTRY_REGEXP = /^([a-zA-Z]+)=([a-z0-9-]+)$/;
const SESSION_EXPIRATION_DELAY = 15 * 60 * 1000;
const SESSION_TIME_OUT_DELAY = 4 * 60 * 60 * 1000;
const SESSION_COOKIE_EXPIRATION_DELAY = 365 * 24 * 60 * 60 * 1000;
const SESSION_NOT_TRACKED = '0';
const TRACKED_WITH_SESSION_REPLAY = '1';
const TRACKED_WITHOUT_SESSION_REPLAY = '2';
const SESSION_KEYS = ['rum', 'id', 'created', 'expire', 'isExpired', 'lock'];
const ACTIVITY_EVENTS = ['click', 'touchstart', 'keydown', 'scroll'];
const ACTIVITY_CHECK_INTERVAL = 60 * 1000;
// Refresh a not-tracked session only when it is close to expiring, rather than on every page or interaction.
const SESSION_EXTEND_WHEN_REMAINING = 10 * 60 * 1000;

type SessionState = Record<string, string>;

const readSessionState = (): SessionState => {
    const cookie = document.cookie.split(/;\s*/).find((entry) => entry.startsWith(`${SESSION_COOKIE_NAME}=`));
    const state: SessionState = {};
    cookie
        ?.slice(SESSION_COOKIE_NAME.length + 1)
        .split(SESSION_ENTRY_SEPARATOR)
        .forEach((entry) => {
            const match = SESSION_ENTRY_REGEXP.exec(entry);
            if (match) state[match[1]] = match[2];
        });
    return state;
};

const writeSessionState = (state: SessionState, now: number) => {
    const value = Object.entries(state)
        .filter(([key]) => key !== 'lock')
        .map(([key, entry]) => `${key}=${entry}`)
        .join(SESSION_ENTRY_SEPARATOR);
    const expires = new Date(now + SESSION_COOKIE_EXPIRATION_DELAY).toUTCString();
    document.cookie = `${SESSION_COOKIE_NAME}=${value};expires=${expires};path=/;samesite=strict`;
};

const isActiveSession = (state: SessionState, now: number) =>
    state.isExpired === undefined &&
    (state.created === undefined || now - Number(state.created) < SESSION_TIME_OUT_DELAY) &&
    (state.expire === undefined || now < Number(state.expire));

const isActiveNotTrackedSession = (state: SessionState, now: number) =>
    state.rum === SESSION_NOT_TRACKED && isActiveSession(state, now);

const extendNotTrackedSession = (state: SessionState, now: number) => {
    if (state.expire !== undefined && Number(state.expire) - now < SESSION_EXTEND_WHEN_REMAINING) {
        writeSessionState({ ...state, expire: String(now + SESSION_EXPIRATION_DELAY) }, now);
    }
};

const performDraw = (sampleRate: number, random: () => number) => sampleRate !== 0 && random() * 100 <= sampleRate;

const generateUUID = () => {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Decides whether this browser session is tracked by RUM without loading the SDK. An active `_dd_s` session
 * keeps its decision. Otherwise the session is drawn the way the SDK draws it and stored in the SDK's format,
 * so later pages and the SDK (which keeps an existing session instead of drawing again) agree with it.
 */
export const isRumSessionSampled = (
    { sessionSampleRate, sessionReplaySampleRate }: { sessionSampleRate: number; sessionReplaySampleRate: number },
    now = Date.now(),
    random = Math.random,
): boolean => {
    try {
        const state = readSessionState();
        const { rum } = state;

        if (
            isActiveSession(state, now) &&
            (rum === SESSION_NOT_TRACKED || rum === TRACKED_WITH_SESSION_REPLAY || rum === TRACKED_WITHOUT_SESSION_REPLAY)
        ) {
            if (rum !== SESSION_NOT_TRACKED) return true;
            extendNotTrackedSession(state, now);
            return false;
        }

        // The SDK gives every new session an anonymous id (trackAnonymousUser defaults to true) and keeps
        // keys owned by other products.
        const retained: SessionState = Object.fromEntries(
            Object.entries(state).filter(([key]) => !SESSION_KEYS.includes(key)),
        );
        retained.aid ??= generateUUID();
        const expire = String(now + SESSION_EXPIRATION_DELAY);

        if (!performDraw(sessionSampleRate, random)) {
            writeSessionState({ rum: SESSION_NOT_TRACKED, expire, ...retained }, now);
            return false;
        }

        writeSessionState(
            {
                rum: performDraw(sessionReplaySampleRate, random)
                    ? TRACKED_WITH_SESSION_REPLAY
                    : TRACKED_WITHOUT_SESSION_REPLAY,
                id: generateUUID(),
                created: String(now),
                expire,
                ...retained,
            },
            now,
        );
        return true;
    } catch {
        // Without a readable session, let the SDK load and draw for itself.
        return true;
    }
};

/**
 * Keeps a not-tracked session alive while the user is active, as the SDK does, so active users on
 * client-side navigations are not drawn again every 15 minutes.
 */
export const extendNotTrackedSessionOnActivity = (now = Date.now) => {
    let lastCheck = 0;
    const onActivity = () => {
        const time = now();
        if (time - lastCheck < ACTIVITY_CHECK_INTERVAL) return;
        lastCheck = time;
        try {
            const state = readSessionState();
            if (isActiveNotTrackedSession(state, time)) extendNotTrackedSession(state, time);
        } catch {
            // Session upkeep is best effort.
        }
    };

    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') onActivity();
    };

    ACTIVITY_EVENTS.forEach((eventName) =>
        window.addEventListener(eventName, onActivity, { capture: true, passive: true }),
    );
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
        ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, onActivity, { capture: true }));
        document.removeEventListener('visibilitychange', onVisibilityChange);
    };
};
